import { PALETTE, srect, stext, rand } from './sketch.js';
import { buildTileCache } from './tiles_art.js';
import { drawProp } from './props_art.js';

/**
 * 渲染器：负责把场景画到 canvas。
 * 分层：地面瓦片 -> 阴影 -> 按 y 排序的物件/角色 -> 焦点高亮 -> 昼夜滤镜 -> 屏幕层提示
 */
export class Renderer {
  constructor(canvas, { tileSize = 40 } = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.tileSize = tileSize;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.tileCache = buildTileCache(tileSize, 4);
    this.showGrid = false;
    this.overlay = null;   // 世界坐标里的额外图层（布置模式用）
    this.view = {
      tileSize,
      daylight: 1,
      time: 0,
      focus: null,
    };
    this._resize();
    window.addEventListener('resize', () => this._resize());
  }

  _resize() {
    // 内部分辨率固定，靠 CSS 缩放，保证不同屏幕观感一致
    const w = this.canvas.width;
    const h = this.canvas.height;
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.viewW = w;
    this.viewH = h;
  }

  render(scene, camera, engine) {
    const ctx = this.ctx;
    const { tileSize } = this;
    this.view.time = engine ? engine.elapsed : 0;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = PALETTE.paper;
    ctx.fillRect(0, 0, this.viewW, this.viewH);

    camera.apply(ctx);

    this._drawTiles(ctx, scene, camera);
    if (this.showGrid) this._drawGrid(ctx, scene);

    // 收集需要按 y 排序绘制的对象
    const items = [];
    for (const p of scene.props) {
      items.push({ y: (p.gy + p.h) * tileSize, kind: 'prop', ref: p });
    }
    for (const e of scene.entities.values()) {
      if (e.dead || !e.drawable) continue;
      items.push({ y: e.drawable.sortY !== undefined ? e.drawable.sortY : (e.transform ? e.transform.y : 0), kind: 'entity', ref: e });
    }
    items.sort((a, b) => a.y - b.y);

    for (const it of items) {
      if (it.kind === 'prop') drawProp(ctx, it.ref, this.view);
      else it.ref.drawable.draw(ctx, it.ref, this.view);
    }

    this._drawFocus(ctx, scene);

    if (this.overlay) this.overlay(ctx, camera);

    camera.restore(ctx);

    // 昼夜滤镜（屏幕空间）
    const dl = this.view.daylight;
    if (dl < 1) {
      ctx.save();
      ctx.globalAlpha = (1 - dl) * 0.5;
      ctx.fillStyle = PALETTE.night;
      ctx.fillRect(0, 0, this.viewW, this.viewH);
      ctx.restore();
    }

    // 屏幕层：交互提示气泡
    if (this.view.focus) {
      const s = camera.worldToScreen(this.view.focus.cx, this.view.focus.cy);
      this._bubble(ctx, s.x, s.y, this.view.focus.label, this.view.focus.hint);
    }
  }

  _drawTiles(ctx, scene, camera) {
    const tm = scene.tilemap;
    if (!tm) return;
    const ts = this.tileSize;
    const x0 = Math.max(0, Math.floor(camera.x / ts) - 1);
    const y0 = Math.max(0, Math.floor(camera.y / ts) - 1);
    const x1 = Math.min(tm.w - 1, Math.ceil((camera.x + camera.viewW) / ts) + 1);
    const y1 = Math.min(tm.h - 1, Math.ceil((camera.y + camera.viewH) / ts) + 1);
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const key = tm.at(tx, ty);
        const list = this.tileCache.get(key) || this.tileCache.get('void');
        if (!list) continue;
        const v = (tx * 31 + ty * 17) % list.length;
        ctx.drawImage(list[v], tx * ts, ty * ts);
      }
    }
  }

  _drawGrid(ctx, scene) {
    const tm = scene.tilemap;
    if (!tm) return;
    const ts = this.tileSize;
    ctx.save();
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = '#999';
    ctx.lineWidth = 0.5;
    for (let y = 0; y <= tm.h; y++) {
      ctx.beginPath(); ctx.moveTo(0, y * ts); ctx.lineTo(tm.w * ts, y * ts); ctx.stroke();
    }
    for (let x = 0; x <= tm.w; x++) {
      ctx.beginPath(); ctx.moveTo(x * ts, 0); ctx.lineTo(x * ts, tm.h * ts); ctx.stroke();
    }
    // 阻挡格标记
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = '#d9705e';
    for (let y = 0; y < tm.h; y++) {
      for (let x = 0; x < tm.w; x++) {
        if (tm.isSolid(x, y)) ctx.fillRect(x * ts, y * ts, ts, ts);
      }
    }
    ctx.restore();
  }

  _drawFocus(ctx, scene) {
    const f = this.view.focus;
    if (!f || !f.prop) return;
    const ts = this.tileSize;
    const x = f.prop.gx * ts, y = f.prop.gy * ts;
    const w = f.prop.w * ts, h = f.prop.h * ts;
    ctx.save();
    ctx.setLineDash([6, 5]);
    ctx.lineDashOffset = -this.view.time * 18;
    ctx.strokeStyle = PALETTE.cloth;
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 2, y + 2, w - 4, h - 4);
    ctx.restore();
  }

  _bubble(ctx, sx, sy, label, hint) {
    const text = hint ? `${label}　${hint}` : label;
    ctx.save();
    ctx.font = '13px "LXGW WenKai Lite","Kaiti SC","STKaiti","KaiTi",serif';
    const w = ctx.measureText(text).width + 22;
    const h = 26;
    const x = sx - w / 2;
    const y = sy - 44;
    ctx.fillStyle = 'rgba(255,252,244,0.94)';
    ctx.strokeStyle = PALETTE.ink;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 8);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = PALETTE.ink;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, sx, y + h / 2 + 1);
    ctx.restore();
  }
}
